/**
 * JWT storage + helpers for the Capacitor mobile bundle.
 *
 * The mobile app authenticates against the cloud Django via SimpleJWT
 * (POST /api/token/obtain/) and stashes the access + refresh tokens
 * in localStorage. Every API request from the React app then sends
 * ``Authorization: Bearer <access>`` via the ApiProvider's commonHeaders
 * — see ``src/providers/ApiProvider.tsx``.
 *
 * Web (browser) users at /user/login/ keep using session cookies and
 * never touch this storage; the bundle treats absent tokens as
 * "session-cookie auth in effect" and skips the Authorization header.
 *
 * Step 5 of the mobile pivot will move this from localStorage into
 * IndexedDB (so the offline outbox + tokens share one durable store);
 * the public surface (``getAccessToken``, ``setTokens``, ``clearTokens``)
 * stays the same so callers don't change.
 */

const ACCESS_KEY = "afri:jwt:access";
const REFRESH_KEY = "afri:jwt:refresh";

const safeGet = (key: string): string | null => {
  try {
    return typeof localStorage !== "undefined" ? localStorage.getItem(key) : null;
  } catch {
    return null;
  }
};

const safeSet = (key: string, value: string): void => {
  try {
    if (typeof localStorage !== "undefined") localStorage.setItem(key, value);
  } catch {
    /* private browsing / quota / etc. — silently no-op */
  }
};

const safeRemove = (key: string): void => {
  try {
    if (typeof localStorage !== "undefined") localStorage.removeItem(key);
  } catch {
    /* same as above */
  }
};

export const getAccessToken = (): string | null => safeGet(ACCESS_KEY);

export const getRefreshToken = (): string | null => safeGet(REFRESH_KEY);

export const setTokens = (access: string, refresh?: string | null): void => {
  if (access) safeSet(ACCESS_KEY, access);
  if (refresh) safeSet(REFRESH_KEY, refresh);
  // Stamp when this session's tokens were minted — the API layer
  // grants a short post-login grace window during which a 401 is
  // treated as transient (token propagation/clock skew) instead of
  // fatal. Without it a stray 401 right after sign-in cleared the
  // fresh tokens and bounced straight back to the login page
  // ("had to log in twice").
  try {
    safeSet("afri:jwt:issued-at", String(Date.now()));
  } catch (_) {
    /* ignore */
  }
};

export const tokensJustIssued = (windowMs = 15_000): boolean => {
  const raw = safeGet("afri:jwt:issued-at");
  if (!raw) return false;
  const t = Number(raw);
  return Number.isFinite(t) && Date.now() - t < windowMs;
};

export const clearTokens = (): void => {
  safeRemove(ACCESS_KEY);
  safeRemove(REFRESH_KEY);
};

/**
 * Wipe the optimistic identity cache — the menubar avatar
 * (``afri:last-avatar``) and the boot-fallback user (``afri:last-user``,
 * read by ``AuthProvider`` while the live ``whoami`` is still in flight).
 *
 * MUST run at every account boundary — sign-OUT and sign-IN both — or
 * the NEXT account inherits the PREVIOUS user's identity during the
 * post-login boot window (before the live whoami resolves), which on
 * web briefly paints the wrong account's menubar and, worse, its
 * privileged nav (a signed-out staff account leaving ``afri:last-user``
 * behind made a following non-staff sign-in momentarily show Platform).
 * The server still authorises correctly; this only stops the UI from
 * showing a stale, wrong-privilege identity for that window.
 */
export const clearCachedIdentity = (): void => {
  safeRemove("afri:last-user");
  safeRemove("afri:last-avatar");
};

export const hasTokens = (): boolean => !!getAccessToken();

/**
 * Build the headers blob the API proxy injects on every request.
 * Returns ``{}`` when there's no access token so the existing
 * cookie-session auth path keeps working unchanged for web users.
 */
export const authHeaders = (): Record<string, string> => {
  const access = getAccessToken();
  return access ? { Authorization: `Bearer ${access}` } : {};
};

/**
 * Resolve a relative ``/api/...`` path against the configured cloud
 * base URL. Used by the bare-fetch call sites (login/signup forms,
 * offline-sync's pull) so the same code works whether the WebView
 * origin is Django (relative paths resolve naturally) or the
 * bundled-SPA file:// origin (relative paths break — need the
 * absolute cloud URL injected from APP_SETTINGS.hostname).
 *
 * If APP_SETTINGS isn't populated or hostname is empty, returns the
 * path unchanged so web + desktop stay on relative paths exactly as
 * before.
 */
export const apiUrl = (path: string): string => {
  if (typeof window === "undefined") return path;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const settings = (window as any).APP_SETTINGS as { hostname?: string } | undefined;
  const base = (settings?.hostname || "").replace(/\/$/, "");
  if (!base) return path;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${base}${path.startsWith("/") ? "" : "/"}${path}`;
};
